import type { LucideIcon } from 'lucide-react'
import { Check } from 'lucide-react'

import { cn } from '@/lib/utils'

export interface TypeCardOption<Value extends string> {
  value: Value
  label: string
  description: string
  icon: LucideIcon
}

interface TypeCardRadioGroupProps<Value extends string> {
  id: string
  label: string
  options: readonly TypeCardOption<Value>[]
  value: Value
  onChange: (value: Value) => void
}

export function TypeCardRadioGroup<Value extends string>({
  id,
  label,
  options,
  value,
  onChange,
}: TypeCardRadioGroupProps<Value>) {
  return (
    <div>
      <span id={`${id}-label`} className="sr-only">
        {label}
      </span>
      <div
        id={id}
        role="radiogroup"
        aria-labelledby={`${id}-label`}
        className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-3"
      >
        {options.map(
          ({ value: optionValue, label: optionLabel, description, icon: Icon }) => {
            const isSelected = value === optionValue
            return (
              <button
                key={optionValue}
                type="button"
                role="radio"
                aria-checked={isSelected}
                onClick={() => onChange(optionValue)}
                className={cn(
                  'flex items-start gap-3 rounded-xl border p-3.5 text-left transition-colors sm:gap-3.5 sm:p-4',
                  isSelected
                    ? 'border-primary bg-primary/5'
                    : 'bg-card hover:border-foreground/30',
                )}
              >
                <div className="bg-muted flex size-9 shrink-0 items-center justify-center rounded-[10px]">
                  <Icon size={16} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold">{optionLabel}</div>
                  <div className="text-muted-foreground mt-1 text-[13px] leading-snug">
                    {description}
                  </div>
                </div>
                <Check
                  size={16}
                  className={cn(
                    'text-primary mt-0.5 shrink-0',
                    !isSelected && 'invisible',
                  )}
                  aria-hidden="true"
                />
              </button>
            )
          },
        )}
      </div>
    </div>
  )
}
